import {DirectionChange, TherapyState} from "../therapy-configuration";

let speed = 1; // Speed from 1 to 10
let lightBallPosition = 50;
let isStopRequested = false;
let elementToChangePosition: any = null;
let directionChange = 'linear'; // 'linear', 'smooth', 'saccade'
let addToCyclesFunction:any;

export function calculateLightBallPosition(currentSessionTime: number, speed: number, directionChange: string): number {
    const cycleDuration = 20 / speed;
    const cycleProgress = (currentSessionTime % cycleDuration) / cycleDuration;
    const isFirstCycle = currentSessionTime < cycleDuration;

    return positionForPhase(cycleProgress, directionChange, isFirstCycle);
}

// Maps a normalised cycle phase (0..1) to a 0-100 bar position for the given
// movement mode. Single source of truth for both the pure time-based helper
// above (used by tests) and the phase-accumulating animation loop below.
function positionForPhase(cycleProgress: number, directionChange: string, isFirstCycle: boolean): number {
    switch (directionChange) {
        case 'smooth':
            return calculateSmoothPosition(cycleProgress, isFirstCycle);
        case 'saccade':
            return calculateSaccadePosition(cycleProgress);
        case 'linear':
        default:
            return calculateLinearPosition(cycleProgress);
    }
}

function calculateLinearPosition(cycleProgress: number): number {
    if (cycleProgress < 0.25) {
        return 50 + 200 * cycleProgress; // 50% to 100%
    } else if (cycleProgress < 0.75) {
        return 100 - 200 * (cycleProgress - 0.25); // 100% to 0%
    } else {
        return 0 + 200 * (cycleProgress - 0.75); // 0% to 50%
    }
}

function calculateSmoothPosition(cycleProgress: number, isFirstCycle: boolean): number {
    const easeInOutQuad = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    // Handle the first transition from 50% to 100% smoothly
    if (isFirstCycle) {
        if (cycleProgress < 0.5) {
            return 50 + 50 * easeInOutQuad(cycleProgress * 2); // 50% to 100%
        } else {
            return 100 - 100 * easeInOutQuad((cycleProgress - 0.5) * 2); // 100% to 0%
        }
    }

    // After the first cycle, go from 0% to 100% and back to 0% smoothly
    if (cycleProgress < 0.5) {
        return 100 * easeInOutQuad(cycleProgress * 2); // 0% to 100%
    } else {
        return 100 - 100 * easeInOutQuad((cycleProgress - 0.5) * 2); // 100% to 0%
    }
}

function calculateSaccadePosition(cycleProgress: number): number {
    if (cycleProgress < 0.5) {
        return 100; // Appear at 100%
    } else {
        return 0; // Appear at 0%
    }
}

// --- Animation loop -------------------------------------------------------
// The loop tracks a continuously accumulated `phase` (0..1) instead of deriving
// it from absolute elapsed time. Advancing the phase by `dt / cycleDuration`
// each frame means a mid-session speed change only alters the *rate* of future
// movement — the ball keeps its current position instead of snapping back to the
// centre. `startTimer` updates speed/direction in place while running and never
// restarts the loop; only a fresh start (or stop) resets the phase.

let lastTimestamp: number | null = null;
let phase = 0;
let hasCompletedFirstCycle = false;
let isRunning = false;
let previousLightBallPosition:number = 0;
let currentDirection = 1;

function animateLightBall(timestamp: number) {
    if (isStopRequested) {
        finishStop();
        return;
    }

    if (lastTimestamp === null) {
        lastTimestamp = timestamp;
    }
    const dt = (timestamp - lastTimestamp) / 1000; // seconds since last frame
    lastTimestamp = timestamp;

    const cycleDuration = 20 / speed;
    phase += dt / cycleDuration;
    if (phase >= 1) {
        phase -= Math.floor(phase);
        hasCompletedFirstCycle = true;
    }

    // Calculate the position from the accumulated phase
    lightBallPosition = positionForPhase(phase, directionChange, !hasCompletedFirstCycle);

    if(directionChange === DirectionChange.saccade) {
        if(previousLightBallPosition && previousLightBallPosition === 100 && lightBallPosition === 0) {
            addToCyclesFunction();
        }
    } else {
        if (lightBallPosition > previousLightBallPosition && currentDirection < 0) {
            addToCyclesFunction();
            currentDirection = 1;
        } else if (lightBallPosition > previousLightBallPosition) {
            currentDirection = 1;
        } else if (lightBallPosition < previousLightBallPosition) {
            currentDirection = -1;
        }
     }


    previousLightBallPosition = lightBallPosition;
    // Update the light ball position
    if (elementToChangePosition && elementToChangePosition.current) {
        elementToChangePosition.current.style.left = `${lightBallPosition}%`;
    }

    // Request the next animation frame
    requestAnimationFrame(animateLightBall);
}

function resetMotionState() {
    lastTimestamp = null;
    phase = 0;
    hasCompletedFirstCycle = false;
    previousLightBallPosition = 0;
    currentDirection = 1;
    lightBallPosition = 50;
}

function finishStop() {
    isRunning = false;
    resetMotionState();
    if (elementToChangePosition && elementToChangePosition.current) {
        elementToChangePosition.current.style.left = `50%`;
    }
    setTimeout(() => {
        isStopRequested = false;
    }, 100);
}

export function startTimer(element: any, desiredSpeed = 1, desiredDirectionChange = 'linear', state:TherapyState = TherapyState.inProgress, addToCycles:any) {
    elementToChangePosition = element;
    addToCyclesFunction = addToCycles;
    // Apply the requested parameters. When the loop is already running these take
    // effect on the next frame WITHOUT resetting the phase, so changing speed or
    // direction mid-session keeps the ball where it is (no snap to centre).
    speed = desiredSpeed;
    directionChange = desiredDirectionChange;

    if(state === TherapyState.inProgress) {
        // Cancel any stop that was queued but not yet applied, so a quick
        // stop→start (or a re-render) doesn't kill the running loop.
        isStopRequested = false;
        if (!isRunning) {
            // Fresh start of a set — begin from the centre.
            isRunning = true;
            resetMotionState();
            requestAnimationFrame(animateLightBall);
        }
    } else {
        stopTimer();
    }
}

export function stopTimer() {
    if (!isRunning) {
        // Nothing animating — just make sure state is clean.
        resetMotionState();
        return;
    }
    // Let the running loop finish on its next frame, which recentres the ball.
    isStopRequested = true;
}
